import { memo, useEffect, useRef, useState } from 'react';
import {
  Handle,
  NodeResizer,
  Position,
  type NodeProps,
  type ResizeParams,
} from '@xyflow/react';
import { activeSheet, useStore, type BlockNode as TBlockNode } from '../store';

function BlockNodeImpl({ id, data, selected }: NodeProps<TBlockNode>) {
  const setBlockField = useStore((s) => s.setBlockField);
  const sheetKind = useStore((s) => activeSheet(s).kind);
  const [editingField, setEditingField] = useState<'title' | 'subtitle' | null>(null);
  const [liveSize, setLiveSize] = useState<{ width: number; height: number } | null>(null);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingField) {
      ref.current?.focus();
      ref.current?.select();
    }
  }, [editingField]);

  const bg = data.color ?? '#1e40af';
  const width = liveSize?.width ?? data.width ?? 200;
  const height = liveSize?.height ?? data.height ?? 96;
  const textColor = data.textColor ?? '#ffffff';
  const shape = data.shape ?? 'rounded';
  const showConnectionHandles = sheetKind !== 'mechanical';
  const radius =
    shape === 'rectangle' || shape === 'diamond' || shape === 'hexagon'
      ? 0
      : shape === 'pill' || shape === 'ellipse'
        ? 999
        : 8;
  const clipPath =
    shape === 'diamond'
      ? 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)'
      : shape === 'hexagon'
        ? 'polygon(14% 0, 86% 0, 100% 50%, 86% 100%, 14% 100%, 0 50%)'
        : undefined;

  const onResize = (_: unknown, params: ResizeParams) => {
    setLiveSize({
      width: Math.round(params.width),
      height: Math.round(params.height),
    });
  };

  const onResizeEnd = (_: unknown, params: ResizeParams) => {
    const next = {
      width: Math.round(params.width),
      height: Math.round(params.height),
    };
    setLiveSize(null);
    setBlockField(id, next);
  };

  return (
    <div
      onDoubleClick={(e) => {
        e.stopPropagation();
        setEditingField('title');
      }}
      style={{
        width,
        height,
        position: 'relative',
        color: textColor,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px 14px',
        cursor: editingField ? 'text' : 'move',
        boxSizing: 'border-box',
      }}
    >
      <NodeResizer
        isVisible={selected}
        minWidth={80}
        minHeight={52}
        color="#2f81f7"
        handleStyle={{
          width: 9,
          height: 9,
          borderRadius: 2,
          background: '#ffffff',
          border: '2px solid #2f81f7',
        }}
        lineStyle={{ borderColor: '#2f81f7' }}
        onResize={onResize}
        onResizeEnd={onResizeEnd}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: bg,
          border: selected
            ? '2px solid #2f81f7'
            : '1px solid rgba(15, 23, 42, 0.25)',
          borderRadius: radius,
          clipPath,
          boxShadow: selected
            ? '0 0 0 3px rgba(47, 129, 247, 0.25), 0 6px 16px rgba(15, 23, 42, 0.14)'
            : '0 4px 10px rgba(15, 23, 42, 0.14)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: shape === 'diamond' ? '68%' : shape === 'hexagon' ? '78%' : '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 0,
          textAlign: 'center',
        }}
      >
        {editingField === 'title' ? (
          <input
            ref={ref}
            defaultValue={data.title}
            onBlur={(e) => {
              setBlockField(id, { title: e.target.value });
              setEditingField(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              else if (e.key === 'Escape') setEditingField(null);
              e.stopPropagation();
            }}
            className="nodrag nopan"
            style={{
              background: 'rgba(255,255,255,0.15)',
              color: textColor,
              border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: 4,
              padding: '3px 6px',
              fontSize: 14,
              fontWeight: 700,
              textAlign: 'center',
              outline: 'none',
              width: '100%',
              boxSizing: 'border-box',
            }}
          />
        ) : (
          <div
            style={{
              fontWeight: 700,
              fontSize: 14,
              textAlign: 'center',
              lineHeight: 1.2,
              wordBreak: 'break-word',
            }}
          >
            {data.title || (
              <span style={{ opacity: 0.55, fontStyle: 'italic' }}>New block</span>
            )}
          </div>
        )}

        {editingField === 'subtitle' ? (
          <input
            ref={ref}
            defaultValue={data.subtitle ?? ''}
            onBlur={(e) => {
              setBlockField(id, { subtitle: e.target.value });
              setEditingField(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              else if (e.key === 'Escape') setEditingField(null);
              e.stopPropagation();
            }}
            className="nodrag nopan"
            style={{
              background: 'rgba(255,255,255,0.15)',
              color: textColor,
              border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: 4,
              padding: '2px 5px',
              fontSize: 11,
              textAlign: 'center',
              outline: 'none',
              width: '100%',
              marginTop: 6,
              boxSizing: 'border-box',
            }}
          />
        ) : (
          <div
            onDoubleClick={(e) => {
              e.stopPropagation();
              setEditingField('subtitle');
            }}
            style={{
              fontSize: 11,
              opacity: 0.85,
              marginTop: 4,
              textAlign: 'center',
              cursor: 'text',
            }}
          >
            {data.subtitle}
          </div>
        )}
      </div>

      {showConnectionHandles && (
        <>
          <Handle type="source" position={Position.Top} id="t" style={handleStyle} />
          <Handle type="source" position={Position.Right} id="r" style={handleStyle} />
          <Handle type="source" position={Position.Bottom} id="b" style={handleStyle} />
          <Handle type="source" position={Position.Left} id="l" style={handleStyle} />
          <Handle type="target" position={Position.Top} id="t" style={{ ...handleStyle, opacity: 0 }} />
          <Handle type="target" position={Position.Right} id="r" style={{ ...handleStyle, opacity: 0 }} />
          <Handle type="target" position={Position.Bottom} id="b" style={{ ...handleStyle, opacity: 0 }} />
          <Handle type="target" position={Position.Left} id="l" style={{ ...handleStyle, opacity: 0 }} />
        </>
      )}
    </div>
  );
}

const handleStyle: React.CSSProperties = {
  width: 10,
  height: 10,
  background: '#ffffff',
  border: '2px solid #0f172a',
};

export const BlockNode = memo(BlockNodeImpl);
